// ============================================================================
// document-type-classifier.js
//
// Deterministic document KIND classification used at Stage 1 import time.
// Distinct from `document-classifier.js` (which decides depth tier:
// skim / standard / deep_analysis). This module decides WHAT a document IS,
// returning a rich `{ class, confidence, evidence, signals }` so downstream
// surfaces (Sorting Floor, RFQ pre-flight gate, prompt routing) can reason
// about WHY a doc was classified.
//
// Classes returned:
//   'rfq'              — Request for Quote (the trigger doc from a client/PM)
//   'our_past_quote'   — A previous QuoteMaker quote authored by THIS tenant
//   'competitor_quote' — A quote from another builder (provided as reference)
//   'estimator_notes'  — The business's OWN priced working notes: operations,
//                        quantities and the estimator's sums. Decided by the
//                        text alone, never the file name (CN1), after the
//                        shared rules and ahead of their quote result (D6).
//   'plan'             : Architectural/engineering drawing, by its text (detectPlanByContent)
//   'report'           : Inspection/dilapidation/condition/engineer report, by its text (detectReportByContent)
//   'other'            — Anything else (notes, generic correspondence, brief)
//
// Designed for the 2026-05-02 RFQ pre-flight gate (Peter): if zero documents
// classify as 'rfq' the auto-pipeline must NOT fire. Reference quotes (own +
// competitor) and plans/reports are valid CONTEXT but never satisfy the gate.
//
// CN2 (Peter 2026-10-13: "file names shouldnt dictate anything. only the
// contents."): 'rfq' is decided by the text alone (detectRfqByContent), first,
// before any rule that reads the file name. Neither the file name, its
// extension nor the folder it was filed in can make a document the RFQ or stop
// it being one.
//
// CN3: 'report' and 'plan' are decided by the text too (detectReportByContent,
// detectPlanByContent), after the RFQ. The report, drawing, DBP issue and
// design declaration file-name rules were removed.
//
// Cheap: pure regex + first-8KB scan. Zero LLM calls. Replaces the old
// `inferDocumentType(filename, content)` that lived in Stage1DataEntry.jsx —
// that returned a bare string; this returns evidence so the Sorting Floor
// can show "WHY rfq?" / "WHY competitor_quote?" inline.
// ============================================================================

import { getInstanceOwner, getInstanceOwnerCached } from './tenant-resolver';
import { detectPlanByContent, detectReportByContent } from '../utils/report-plan-content-detector';
import { detectEstimatorPricedNotes } from '../utils/estimator-notes-detector';
import { detectRfqByContent, type RfqContentKind, type RfqContentRejection } from '../utils/rfq-content-detector';

export type DocumentKindClass = 'rfq' | 'our_past_quote' | 'competitor_quote' | 'estimator_notes' | 'plan' | 'report' | 'other';
export type LegacyDocumentKindClass = 'rfq' | 'competitor' | 'our_past_quote' | 'estimator_notes' | 'plan' | 'report' | 'other';

export interface DocumentTypeSignals {
    /** The text reads as the client's request for a quote (detectRfqByContent). */
    rfqByContent: boolean;
    rfqKind: RfqContentKind | null;
    /** What the text reads as instead, when it is positively not the request. */
    rfqRejectedAs: RfqContentRejection | null;
    /** RFQ phrasing somewhere in the text ("please quote", "scope of works"). */
    rfqContent: boolean;
    formalRfqHeading: boolean;
    requestAuthorityContent: boolean;
    pmAuthor: boolean;
    claimAuthor: boolean;
    quoteContent: boolean;
    priceLineItems: number;
    ourIdentityHit: string | null;
    estimatorNotes: boolean;
    plan: boolean;
    report: boolean;
    designDeclaration: boolean;
    regulatedTechnicalPlan: boolean;
}

export interface DocumentTypeClassificationResult {
    class: DocumentKindClass;
    confidence: number;
    evidence: string[];
    signals: DocumentTypeSignals;
}

export interface DocumentTypeClassifierOptions {
    brandName?: string | null;
    businessName?: string | null;
    abn?: string | null;
}

interface TenantIdentityRegexes {
    namePart: RegExp | null;
    abnRe: RegExp | null;
}

// ─── No file-name patterns ──────────────────────────────────────────────────
// The file name is never read (CN2 to CN6). CN2 removed the RFQ and request
// authority file-name patterns and the RFQ package folder rule. CN6 removed
// the last two: a quote-like name ("quote", "invoice", "estimate",
// "proposal") that added weight to the quote reading, and a competitor hint
// that only added evidence text. Measured on 3,401 documents (RJ Bird's files
// and pastes, and 44 synthetic), the name changed the class of one: RJ Bird's
// own tax invoice, which the name made a past quote
// (docs/contents-not-names/CN6-results-2026-10-14.md).

// A Design and Building Practitioners (DBP) drawing set and its Design
// Compliance Declaration are related records, but they are not interchangeable:
// the drawing/specification is technical method authority; the declaration is
// evidence that a separately identified regulated design was declared. Keep the
// roles distinct before considering generic plan/report or RFQ signals. Both
// are read from the text (CN3); the declaration and "DBP Issue" file-name
// rules were removed.
const DESIGN_DECLARATION_CONTENT_RE = /\bdesign\s+compliance\s+declaration(?:\s*-\s*single\s+regulated\s+design)?\b/i;
const REGULATED_DESIGN_RECORD_RE = /\bregulated\s+design\s+record\b/i;
const TECHNICAL_PLAN_CONTENT_MARKERS = [
    /\bdrawing\s+title\b/i,
    /\bdrawing\s+(?:no\.?|number)\b/i,
    /\bscale\s*1\s*[:/]\s*\d+\b/i,
    /\bspecification\s+notes\s+and\s+plan\b/i,
    /\bwaterproofing\s+notes\b/i,
];

function isRegulatedTechnicalDesignPlan(content?: string | null): boolean {
    const sample = typeof content === 'string' ? content.slice(0, 8000) : '';
    if (!sample) return false;
    const markerCount = TECHNICAL_PLAN_CONTENT_MARKERS.reduce(
        (count, pattern) => count + (pattern.test(sample) ? 1 : 0),
        0,
    );
    return REGULATED_DESIGN_RECORD_RE.test(sample) && markerCount >= 3;
}

// ─── Content patterns ──────────────────────────────────────────────────────

// RFQ-positive phrases (someone asking us for a price)
const RFQ_CONTENT_RE = /\b(?:quote\s+request(?:\s*[-:#]\s*(?:quote\s+)?(?:no\.?|number|#))?|request\s+for\s+(?:a\s+)?(?:quote|quotation|pricing|proposal|tender)|please\s+(?:can\s+you\s+)?(?:provide|quote|send|submit|supply)(?:\s+(?:us\s+)?(?:with\s+)?(?:a|your|me)\s+)?\s*(?:quote|quotation|pricing|proposal|offer|tender)|kindly\s+(?:provide|quote|send|submit|supply)(?:\s+(?:us\s+)?(?:with\s+)?(?:a|your|me)\s+)?\s*(?:quote|quotation|pricing|proposal)|quotation\s+(?:required|requested|sought)|tender\s+(?:submission|response|required|invited)|scope\s+of\s+works?\b|bill\s+of\s+quantities|please\s+quote\s+(?:for|on)|looking\s+(?:to\s+get|for)\s+(?:a\s+)?(?:quote|quotation)|seeking\s+(?:a\s+)?(?:quote|quotation|proposal)|(?:we|i)\s+(?:require|need|want)\s+(?:a\s+)?(?:quote|quotation|price|estimate|pricing)|rfq\s+(?:for|#)|on\s+behalf\s+of\s+(?:the\s+)?(?:owners?|strata)|attached\s+is\s+(?:the\s+)?(?:scope|brief|rfq)|please\s+(?:find\s+)?attached\s+(?:the\s+)?(?:scope|brief|specification|rfq))/i;

// Formal client systems sometimes title an inbound RFQ "QUOTE REQUEST -
// Quote No. 95901" or "QUOTATION REQUEST". The embedded "Quote No" looks like an
// outgoing quote to QUOTE_CONTENT_RE, so retain this stronger heading signal explicitly.
const FORMAL_RFQ_HEADING_RE = /(?:^|\n|\r)\s*(?:(?:quot(?:e|ation)|pricing|tender|proposal)\s+request|request\s+for\s+(?:a\s+)?(?:quot(?:e|ation)|pricing|tender|proposal)|invitation\s+to\s+(?:quot(?:e|ation)|tender|proposal))\b/im;

const REQUEST_AUTHORITY_CONTENT_RE = /\b(?:works?\s+orders?|purchase\s+orders?|service\s+requests?|job\s+orders?)\b/i;

// Strata/PM identity hints — strong RFQ correlator. Includes informal
// abbreviations ("owners corp" without the full "corporation" suffix).
const PM_AUTHOR_RE = /\b(strata\s+manager|building\s+manager|owners?\s+corp(?:oration)?|body\s+corp(?:orate)?|strata\s+committee|property\s+manager|facilities\s+manager|asset\s+manager)\b/i;

// Insurance / HBCF claim hints — strong RFQ correlator
const CLAIM_AUTHOR_RE = /\b(insurance\s+(?:claim|assessor|company)|hbcf\s+claim|home\s+building\s+compensation|claim\s+(?:no|number|reference)|loss\s+adjuster)\b/i;

// Quote-negative phrases (this is a QUOTE — someone offering pricing, not asking)
// These reliably appear in finished/sent quote documents and rarely in RFQs.
const QUOTE_CONTENT_RE = /\b(?:quotation\s+(?:no|number|#|reference)\s*[:#]?|quote\s+(?:no|number|#|reference)\s*[:#]?|valid(?:ity)?\s+for\s+\d+\s+days?|quote\s+valid\s+(?:for|until)|thank\s+you\s+for\s+the\s+opportunity\s+to\s+(?:quote|tender|provide)|banking\s+details|bsb\s*[:#]?\s*\d|pay(?:ment)?\s+(?:terms|details|schedule)|deposit\s+(?:on\s+acceptance|required|invoice)|gst\s+inclusive\s+total|total\s+(?:inc(?:l(?:usive)?)?|including|incl?)\s+gst|subtotal\s*[:$]|grand\s+total)/i;

// Itemised pricing markers — if a doc has many "$N" amounts it's likely a quote.
const PRICE_LINE_ITEM_RE = /\$\s*\d{1,3}(?:,\d{3})*(?:\.\d{2})?(?:\s+(?:inc|ex|gst|excl|incl))?/gi;

// ─── Tenant identity (our_past_quote vs competitor_quote) ───────────────────

function profileText(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The ACTIVE business's identity, from the tenant profile the app loads at
 * start (legal name, trading name, ABN). When the profile has not loaded, or
 * the install has none, the business is unknown: no name matches, so no
 * document is "ours". Never the compile-time RJ Bird name.
 */
export function activeBusinessIdentity(): Pick<DocumentTypeClassifierOptions, 'brandName' | 'businessName' | 'abn'> {
    let profile: any = null;
    try {
        profile = getInstanceOwnerCached();
    } catch {
        profile = null;
    }
    return {
        brandName: profileText(profile?.legalName),
        businessName: profileText(profile?.tradingName),
        abn: profileText(profile?.abn),
    };
}

/**
 * The active business's identity after the tenant profile has loaded. An
 * async caller awaits this and passes the result to classifyDocumentType, so
 * the business's own past quote is not marked competitor_quote merely because
 * the profile cache had not loaded yet. getInstanceOwner resolves to an empty
 * owner on a failed fetch; the catch covers an install or a test without the
 * resolver. Either way the cached identity (or an unknown business) is used.
 * Sync callers cannot await and keep reading the cache.
 */
export async function loadActiveBusinessIdentity(): Promise<Pick<DocumentTypeClassifierOptions, 'brandName' | 'businessName' | 'abn'>> {
    try {
        await getInstanceOwner();
    } catch {
        // Fall through to whatever the cache holds.
    }
    return activeBusinessIdentity();
}

// Explicit identity options win; otherwise the active business's profile.
function identityOptions(opts: DocumentTypeClassifierOptions): DocumentTypeClassifierOptions {
    const explicit = ['brandName', 'businessName', 'abn']
        .some(key => Object.prototype.hasOwnProperty.call(opts || {}, key));
    return explicit ? opts : { ...opts, ...activeBusinessIdentity() };
}

/**
 * Build the regex set that flags content as authored by US (this tenant).
 * Sourced from the identity options: by default the active business's tenant
 * profile (activeBusinessIdentity). Returns null if neither name nor ABN are
 * usable, which makes every quote someone else's.
 */
function buildOurIdentityRegex({ brandName = null, businessName = null, abn = null }: DocumentTypeClassifierOptions = {}): TenantIdentityRegexes | null {
    const tokens: string[] = [];
    const tryAdd = (s?: string | null) => {
        if (typeof s !== 'string') return;
        const trimmed = s.trim();
        if (trimmed.length >= 4) tokens.push(trimmed);
    };
    tryAdd(brandName);
    tryAdd(businessName);
    // Strip ' Pty Ltd' / ' Pty. Ltd.' so the bare business name alone matches
    // letterhead variants. We deliberately keep the full string too.
    if (typeof brandName === 'string') {
        tryAdd(brandName.replace(/\s+pty\.?\s+ltd\.?$/i, '').trim());
    }
    if (typeof businessName === 'string') {
        tryAdd(businessName.replace(/\s+pty\.?\s+ltd\.?$/i, '').trim());
    }
    // ABN: 11 digits (with or without spaces). Match the tenant's ABN exactly.
    let abnRe: RegExp | null = null;
    if (typeof abn === 'string') {
        const digits = abn.replace(/\D/g, '');
        if (digits.length === 11) {
            abnRe = new RegExp(digits.split('').join('\\s*'), 'i');
        }
    }
    if (tokens.length === 0 && !abnRe) return null;
    const escapedTokens = tokens
        .filter(Boolean)
        .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const namePart = escapedTokens.length
        ? new RegExp(`\\b(?:${escapedTokens.join('|')})\\b`, 'i')
        : null;
    return { namePart, abnRe };
}

/**
 * Test whether content matches OUR identity (this tenant). Returns the
 * matched evidence string (or null). A quote that matches → 'our_past_quote';
 * a quote that does NOT match → 'competitor_quote'.
 */
function matchesOurIdentity(content?: string | null, identityRegex?: TenantIdentityRegexes | null): string | null {
    if (!identityRegex || typeof content !== 'string') return null;
    const sample = content.slice(0, 8000); // letterhead always near the top
    if (identityRegex.namePart) {
        const m = sample.match(identityRegex.namePart);
        if (m) return `tenant-name "${m[0]}"`;
    }
    if (identityRegex.abnRe) {
        const m = sample.match(identityRegex.abnRe);
        if (m) return `tenant-ABN "${m[0]}"`;
    }
    return null;
}

// ─── Estimator notes (D6, CN1) ──────────────────────────────────────────────
//
// The business's OWN priced working notes. CN1 (Peter 2026-10-13: "file names
// shouldnt dictate anything. only the contents."): the class is decided by
// the text alone (detectEstimatorPricedNotes), after every shared rule above
// has run unchanged, and only in place of a result in
// ESTIMATOR_NOTES_REPLACEABLE. The file name and its extension are never
// read for it. Text that carries no estimator prices is never estimator
// notes, whatever the file is called.

// The shared rules whose result priced notes may replace. An RFQ, a plan and
// a report keep their class.
const ESTIMATOR_NOTES_REPLACEABLE: ReadonlySet<DocumentKindClass> = new Set<DocumentKindClass>(['competitor_quote', 'our_past_quote', 'other']);

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Classify a document into one of:
 *   'rfq' | 'our_past_quote' | 'competitor_quote' | 'estimator_notes' | 'plan' | 'report' | 'other'
 *
 * Returns a rich object with confidence + evidence so the Sorting Floor can
 * surface WHY each doc landed where it did, and the RFQ pre-flight gate can
 * present an honest breakdown to the operator.
 *
 * @param {string} _filename  kept for the callers' signature, never read (CN6)
 * @param {string} content    extracted text (first 8KB scanned)
 * @param {object} [opts]
 * @param {string} [opts.brandName]    override the active business's legal name for the our-identity check
 * @param {string} [opts.businessName] additional name token (e.g. trading name)
 * @param {string} [opts.abn]          tenant ABN (11 digits, with or without spaces)
 * @returns {{ class: string, confidence: number, evidence: string[], signals: object }}
 */
export function classifyDocumentType(_filename?: string | null, content?: string | null, opts: DocumentTypeClassifierOptions = {}): DocumentTypeClassificationResult {
    const result = classifySharedDocumentType(content, opts);
    if (!ESTIMATOR_NOTES_REPLACEABLE.has(result.class)) return result;
    const notes = detectEstimatorPricedNotes(content);
    if (!notes.isEstimatorNotes) {
        // Say why only when the text carried a price, so the Sorting Floor
        // can show why priced text was not read as the business's notes.
        if (notes.priceLines > 0) result.evidence.push(`priced lines, but ${notes.evidence.join('; ')}, so not estimator notes`);
        return result;
    }
    result.signals.estimatorNotes = true;
    result.evidence.push(`the text reads as the business's own priced working notes (${notes.evidence.join('; ')}) → estimator_notes (shared rules said ${result.class})`);
    return { ...result, class: 'estimator_notes', confidence: result.class === 'other' ? 70 : 80 };
}

// How sure the class is, by the kind of request the text is.
const RFQ_CONFIDENCE: Record<RfqContentKind, number> = {
    'formal-form': 90,
    'strata-request': 85,
    'email-ask': 80,
};

// The shared rules. The "is this ours" check reads the active business
// (identityOptions, D6). Every decision is the text's alone (CN2 to CN6).
function classifySharedDocumentType(content?: string | null, opts: DocumentTypeClassifierOptions = {}): DocumentTypeClassificationResult {
    const evidence: string[] = [];
    const signals: DocumentTypeSignals = {
        rfqByContent: false,
        rfqKind: null,
        rfqRejectedAs: null,
        rfqContent: false,
        formalRfqHeading: false,
        requestAuthorityContent: false,
        pmAuthor: false,
        claimAuthor: false,
        quoteContent: false,
        priceLineItems: 0,
        ourIdentityHit: null,
        estimatorNotes: false,
        plan: false,
        report: false,
        designDeclaration: false,
        regulatedTechnicalPlan: false,
    };

    const sampleContent = (typeof content === 'string' ? content : '').slice(0, 8000);

    // ─── The client's request, by its text (CN2) ────────────────────────
    // A request is the request whatever it is called and wherever it was filed ("scan001.pdf",
    // an email whose subject names a drawing or a report). The detector itself
    // turns away quotes, reports, the office's inspection requests, invoices,
    // drawings and the estimator's notes by what they say, so a site report
    // that quotes its request is not the request.
    const request = detectRfqByContent(sampleContent);
    if (request.isRfq && request.kind) {
        signals.rfqByContent = true;
        signals.rfqKind = request.kind;
        signals.rfqContent = true;
        signals.formalRfqHeading = request.kind === 'formal-form' && Boolean(request.title) && !request.authority;
        signals.requestAuthorityContent = request.authority;
        signals.pmAuthor = PM_AUTHOR_RE.test(sampleContent);
        signals.claimAuthor = CLAIM_AUTHOR_RE.test(sampleContent);
        evidence.push(`the text reads as the client's request for a quote (${request.kind}: ${request.evidence.join('; ')})`);
        return { class: 'rfq', confidence: RFQ_CONFIDENCE[request.kind], evidence, signals };
    }
    if (request.rejectedAs) {
        signals.rfqRejectedAs = request.rejectedAs;
        evidence.push(`not the client's request: ${request.evidence.join('; ')}`);
    }

    // ─── Plan / report, by the text (CN3) ───────────────────────────────
    // A drawing or a report is reference material, not an RFQ. Neither is
    // decided by the file name: a quote for structural repairs is not a
    // report, and a reply about a strata plan is not a drawing.
    //
    // A DBP declaration may name its associated drawings/specifications, so
    // declaration detection must run before content-aware plan detection.
    if (DESIGN_DECLARATION_CONTENT_RE.test(sampleContent)) {
        signals.report = true;
        signals.designDeclaration = true;
        evidence.push('document is a design compliance declaration for a separately identified regulated design');
        return { class: 'report', confidence: 96, evidence, signals };
    }
    if (isRegulatedTechnicalDesignPlan(sampleContent)) {
        signals.plan = true;
        signals.regulatedTechnicalPlan = true;
        evidence.push('content identifies a regulated technical design drawing/plan with specification title-block markers');
        return { class: 'plan', confidence: 96, evidence, signals };
    }
    const report = detectReportByContent(sampleContent, request);
    if (report.isReport) {
        signals.report = true;
        evidence.push(...report.evidence);
        return { class: 'report', confidence: report.title ? 88 : 80, evidence, signals };
    }
    const drawing = detectPlanByContent({ text: sampleContent, textLength: typeof content === 'string' ? content.length : 0 });
    if (drawing.isPlan) {
        signals.plan = true;
        evidence.push(`the text reads as a drawing (${drawing.evidence.join('; ')})`);
        return { class: 'plan', confidence: drawing.marks.length >= 3 ? 90 : 80, evidence, signals };
    }

    // ─── Content-level signals ───────────────────────────────────────────
    if (sampleContent.length > 30) {
        if (FORMAL_RFQ_HEADING_RE.test(sampleContent)) {
            signals.formalRfqHeading = true;
            evidence.push('content has a formal inbound QUOTE REQUEST heading');
        }
        if (RFQ_CONTENT_RE.test(sampleContent)) {
            signals.rfqContent = true;
            evidence.push('content contains RFQ phrasing (please quote / scope of works / etc.)');
        }
        if (REQUEST_AUTHORITY_CONTENT_RE.test(sampleContent)) {
            signals.requestAuthorityContent = true;
            evidence.push('content contains a work/purchase/service/job order marker');
        }
        if (PM_AUTHOR_RE.test(sampleContent)) {
            signals.pmAuthor = true;
            evidence.push('content authored by strata/PM/owners-corp');
        }
        if (CLAIM_AUTHOR_RE.test(sampleContent)) {
            signals.claimAuthor = true;
            evidence.push('content references insurance/HBCF claim');
        }
        if (QUOTE_CONTENT_RE.test(sampleContent)) {
            signals.quoteContent = true;
            evidence.push('content contains quote phrasing (validity / banking / total inc GST / etc.)');
        }
        const priceMatches = sampleContent.match(PRICE_LINE_ITEM_RE);
        signals.priceLineItems = priceMatches ? priceMatches.length : 0;
        if (signals.priceLineItems >= 4) {
            evidence.push(`content has ${signals.priceLineItems} priced line items (looks like an itemised quote)`);
        }
    }

    // ─── Compute decision ────────────────────────────────────────────────

    // Quote-vs-RFQ disambiguation, from the text (line items, validity
    // wording, banking details). Threshold 2 (was 3) because the
    // QUOTE_CONTENT_RE phrases (validity for N days, banking details, "Total
    // inc GST", "thank you for the opportunity to quote") are extremely
    // specific to outgoing quotes. A quote-like file name no longer adds a
    // point (CN6).
    const quoteWeight =
          (signals.quoteContent ? 2 : 0)
        + (signals.priceLineItems >= 4 ? 2 : 0)
        + (signals.priceLineItems >= 8 ? 1 : 0);

    // Request wording in a text that is not the request (a quote that cites
    // its work order, a report that quotes the request) still keeps weak quote
    // signals from making it a quote, as before CN2. It never makes it the RFQ.
    const requestWeight =
          (signals.rfqContent ? 2 : 0)
        + (signals.requestAuthorityContent ? 3 : 0)
        + (signals.pmAuthor ? 1 : 0)
        + (signals.claimAuthor ? 1 : 0);

    // A finished quote can mention the work/service order it responds to. Two
    // independent outgoing signals in the text (quote wording plus line items,
    // or plus our letterhead) remain decisive even when those request terms
    // appear.
    const identityRegex = buildOurIdentityRegex(identityOptions(opts));
    const ourHit = signals.quoteContent ? matchesOurIdentity(sampleContent, identityRegex) : null;
    signals.ourIdentityHit = ourHit;
    const decisiveOutgoingQuote = !signals.formalRfqHeading && signals.quoteContent && (
        signals.priceLineItems >= 4 || (Boolean(ourHit) && signals.priceLineItems >= 1)
    );

    // If quote signals dominate, classify as a quote and decide WHOSE.
    if (quoteWeight >= 2 && (quoteWeight > requestWeight || decisiveOutgoingQuote)) {
        if (ourHit) {
            evidence.push(`tenant identity matched in content (${ourHit}) → our_past_quote`);
            return { class: 'our_past_quote', confidence: Math.min(95, 60 + quoteWeight * 5), evidence, signals };
        }
        evidence.push('no tenant identity match → competitor_quote');
        return { class: 'competitor_quote', confidence: Math.min(90, 55 + quoteWeight * 5), evidence, signals };
    }

    // Default — uncategorised. Could be inspector notes, generic correspondence.
    return { class: 'other', confidence: 30, evidence, signals };
}

/**
 * Legacy-compatible adapter. Returns a single string class to match the old
 * `inferDocumentType` signature. Existing call sites keep working; new ones
 * should call `classifyDocumentType()` directly to access evidence.
 *
 * Class mapping:
 *   'rfq'              → 'rfq'        (unchanged — gate driver)
 *   'competitor_quote' → 'competitor' (legacy alias to keep old code matching)
 *   'our_past_quote'   → 'our_past_quote' (NEW)
 *   'plan'             → 'plan' (NEW — was 'other' before)
 *   'report'           → 'report' (NEW — was 'other' before)
 *   'other'            → 'other'
 */
export function classifyDocumentTypeLegacy(filename?: string | null, content?: string | null, opts: DocumentTypeClassifierOptions = {}): LegacyDocumentKindClass {
    const result = classifyDocumentType(filename, content, opts);
    if (result.class === 'competitor_quote') return 'competitor';
    return result.class;
}

// Expose patterns for tests that want to verify the regex behaviour without
// going through classifyDocumentType().
export const _patterns = {
    DESIGN_DECLARATION_CONTENT_RE,
    REGULATED_DESIGN_RECORD_RE,
    RFQ_CONTENT_RE,
    FORMAL_RFQ_HEADING_RE,
    REQUEST_AUTHORITY_CONTENT_RE,
    PM_AUTHOR_RE,
    CLAIM_AUTHOR_RE,
    QUOTE_CONTENT_RE,
    PRICE_LINE_ITEM_RE,
};
